import { contactIdForPhone } from "components/_dashboard/listings/seller/sellerPhone";

/**
 * A phone, as some screen happens to hold it → the contact id the lookup store is keyed by.
 *
 * A contact's document id IS its phone in E.164, and the server matches ids exactly. The
 * screens that ask, though, hold phones in every spelling: a call record has `+9725…`, a
 * signer was typed as `052-748 5512`, a calendar attendee came from Google. This is the one
 * place those become an id, so two surfaces showing the same person under two spellings
 * share one answer — and one request.
 *
 * Unparseable input is returned VERBATIM rather than dropped: a handful of legacy contacts
 * live under ids that are not phones at all (measured: 8 of 207,479), and asking about one
 * by its exact id still finds it.
 *
 * Memoised, because `libphonenumber` is not free and a table re-renders its phones on every
 * keystroke of a filter — and because the normaliser logs each number it cannot parse.
 */

const E164 = /^\+[1-9]\d{6,14}$/;

const MAX_MEMO = 5000;
const memo = new Map<string, string>();

export const contactIdForLookup = (phone: string | null | undefined): string | null => {
  const raw = phone?.trim();
  if (!raw) return null;
  // Nearly every phone that reaches here is already an id; it needs no parsing and no memo.
  if (E164.test(raw)) return raw;

  const known = memo.get(raw);
  if (known !== undefined) return known;

  const id = contactIdForPhone(raw) ?? raw;
  // A search box feeds this one keystroke at a time; the cap keeps a long session bounded.
  if (memo.size >= MAX_MEMO) memo.clear();
  memo.set(raw, id);
  return id;
};
